import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';
import { LOG_LEVEL_OPTIONS } from '@shared-ui/logging';
import { IntegerInput } from '@shared-ui/components';

const LOG_PROVIDERS = ['console', 'database', 'server'];

const IOS_COMPONENTS = [
  { code: '70', name: 'app' },
  { code: '71', name: 'api' },
  { code: '72', name: 'auth' },
  { code: '73', name: 'navigation' },
  { code: '74', name: 'canvas' },
  { code: '75', name: 'question' },
  { code: '76', name: 'analysis' },
  { code: '77', name: 'voice' },
  { code: '78', name: 'storage' },
  { code: '79', name: 'progress' },
];

type LoggingProviderConfig = {
  enabled?: boolean;
  defaultLevel?: number;
  componentLevels?: Record<string, number>;
};

type LoggingConfig = {
  retentionDays?: number;
  providers?: Record<string, LoggingProviderConfig>;
} | null;

interface LoggingTabProps {
  userId: string;
}

export function LoggingTab({ userId }: LoggingTabProps) {
  const queryClient = useQueryClient();

  const [loggingConfig, setLoggingConfig] = useState<LoggingConfig>(null);
  const [logSaveStatus, setLogSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);

  const { data: loggingData } = useQuery({
    queryKey: ['userLoggingConfig', userId],
    queryFn: () => adminApi.getUserLoggingConfig(userId),
    enabled: !!userId,
  });

  useEffect(() => {
    if (loggingData) {
      setLoggingConfig(loggingData.loggingConfig);
    }
  }, [loggingData]);

  const saveLoggingMutation = useMutation({
    mutationFn: () => adminApi.updateUserLoggingConfig(userId, loggingConfig),
    onMutate: () => setLogSaveStatus('saving'),
    onSuccess: () => {
      setLogSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['userLoggingConfig', userId] });
      setTimeout(() => setLogSaveStatus('idle'), 2000);
    },
    onError: () => {
      setLogSaveStatus('error');
      setTimeout(() => setLogSaveStatus('idle'), 3000);
    },
  });

  return (
    <div className="features-section">
      <p className="logging-subtitle">
        {loggingConfig ? 'Custom config set for this user.' : 'Using system defaults.'}
      </p>

      {/* Retention */}
      <div className="logging-field">
        <label>Retention Days</label>
        <IntegerInput
          min={1}
          max={30}
          value={loggingConfig?.retentionDays ?? 7}
          onChange={(val) => {
            if (val == null) return;
            setLoggingConfig(prev => ({
              ...prev,
              retentionDays: val,
              providers: prev?.providers,
            }));
          }}
        />
      </div>

      {/* Per-provider config */}
      <div className="logging-providers">
        {LOG_PROVIDERS.map(providerName => {
          const providerConfig = loggingConfig?.providers?.[providerName];
          const isExpanded = expandedProvider === providerName;

          return (
            <div key={providerName} className="logging-provider-card">
              <div className="logging-provider-header" onClick={() => setExpandedProvider(isExpanded ? null : providerName)}>
                <span className="logging-provider-name">{providerName}</span>
                <div className="logging-provider-summary">
                  <label className="logging-toggle-label">
                    <input
                      type="checkbox"
                      checked={providerConfig?.enabled !== false}
                      onChange={(e) => {
                        e.stopPropagation();
                        setLoggingConfig(prev => ({
                          ...prev,
                          providers: {
                            ...prev?.providers,
                            [providerName]: {
                              ...prev?.providers?.[providerName],
                              enabled: e.target.checked,
                            },
                          },
                        }));
                      }}
                    />
                    Enabled
                  </label>
                  <select
                    value={providerConfig?.defaultLevel ?? (providerName === 'server' ? 2 : 3)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setLoggingConfig(prev => ({
                        ...prev,
                        providers: {
                          ...prev?.providers,
                          [providerName]: {
                            ...prev?.providers?.[providerName],
                            defaultLevel: Number(e.target.value),
                          },
                        },
                      }));
                    }}
                  >
                    {LOG_LEVEL_OPTIONS.map(l => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                  <span className="logging-expand-icon">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </div>
              </div>

              {isExpanded && (
                <div className="logging-component-overrides">
                  <div className="logging-component-header">
                    <span>Component</span>
                    <span>Level Override</span>
                  </div>
                  {IOS_COMPONENTS.map(comp => {
                    const compLevel = providerConfig?.componentLevels?.[comp.name];
                    return (
                      <div key={comp.code} className="logging-component-row">
                        <span className="logging-component-name">
                          <span className="logging-component-code">{comp.code}</span>
                          {comp.name}
                        </span>
                        <select
                          value={compLevel ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setLoggingConfig(prev => {
                              const currentProvider = prev?.providers?.[providerName] || {};
                              const currentLevels = { ...currentProvider.componentLevels };
                              if (val === '') {
                                delete currentLevels[comp.name];
                              } else {
                                currentLevels[comp.name] = Number(val);
                              }
                              return {
                                ...prev,
                                providers: {
                                  ...prev?.providers,
                                  [providerName]: {
                                    ...currentProvider,
                                    componentLevels: Object.keys(currentLevels).length > 0 ? currentLevels : undefined,
                                  },
                                },
                              };
                            });
                          }}
                        >
                          <option value="">Inherit default</option>
                          {LOG_LEVEL_OPTIONS.map(l => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="features-actions">
        <button
          className="save-features-btn"
          onClick={() => saveLoggingMutation.mutate()}
          disabled={saveLoggingMutation.isPending}
        >
          {saveLoggingMutation.isPending ? 'Saving...' : 'Save Logging Config'}
        </button>
        <button
          className="reset-btn"
          onClick={() => {
            setLoggingConfig(null);
            saveLoggingMutation.mutate();
          }}
        >
          Reset to Defaults
        </button>
        {logSaveStatus === 'saved' && <span className="status-saved">Saved!</span>}
        {logSaveStatus === 'error' && <span className="status-error">Failed to save</span>}
      </div>
    </div>
  );
}
